import { useState } from 'react'
import { Radio, AlertTriangle, DollarSign, ShieldOff, Calendar } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useDiscoveryContext } from '../DiscoveryWizard'
import { gpsDenialOptions, mitigationOptions } from '@/lib/discovery-data'

export function ChallengeStep() {
  const { state, updateChallenges } = useDiscoveryContext()
  const { challenges } = state

  return (
    <div className="h-full flex flex-col items-center justify-center px-6 py-12 overflow-auto">
      <div className="max-w-2xl w-full space-y-8">
        {/* Header */}
        <div className="text-center wizard-fade-in">
          <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-3">
            Current challenges with GPS
          </h2>
          <p className="text-muted-foreground">
            Help us understand your navigation challenges
          </p>
        </div>

        {/* Questions */}
        <div className="space-y-8">
          {/* GPS Denial Frequency */}
          <SelectCards
            icon={<Radio className="w-5 h-5" />}
            label="How often do you experience GPS issues?"
            sublabel="Jamming, spoofing, or degraded signal"
            options={gpsDenialOptions.map((o) => ({
              value: o.value,
              label: o.label,
              description: o.description,
            }))}
            value={challenges.gpsDenialFrequency}
            onChange={(val) =>
              updateChallenges({
                gpsDenialFrequency: val as 'never' | 'occasional' | 'frequent' | 'constant',
              })
            }
            delay={0}
          />

          {/* Mission Abort Rate */}
          <SliderInput
            icon={<AlertTriangle className="w-5 h-5" />}
            label="Mission abort rate due to GPS issues"
            sublabel="Percentage of missions impacted"
            value={challenges.missionAbortRate}
            onChange={(val) => updateChallenges({ missionAbortRate: val })}
            suffix="%"
            min={0}
            max={100}
            delay={100}
          />

          {/* Cost per Failed Mission */}
          <CurrencyInput
            icon={<DollarSign className="w-5 h-5" />}
            label="Cost per failed mission"
            sublabel="Include lost time, fuel, potential asset damage"
            value={challenges.costPerFailedMission}
            onChange={(val) => updateChallenges({ costPerFailedMission: val })}
            delay={200}
          />

          {/* Annual Missions */}
          <NumberInput
            icon={<Calendar className="w-5 h-5" />}
            label="Annual mission count"
            sublabel="Total missions planned per year"
            value={challenges.annualMissions}
            onChange={(val) => updateChallenges({ annualMissions: val })}
            suffix="missions/year"
            delay={300}
          />

          {/* Current Mitigation */}
          <SelectCards
            icon={<ShieldOff className="w-5 h-5" />}
            label="Current GPS backup approach"
            sublabel="How do you handle GPS failures today?"
            options={mitigationOptions.map((o) => ({
              value: o.value,
              label: o.label,
              description: o.description,
            }))}
            value={challenges.currentMitigation}
            onChange={(val) =>
              updateChallenges({
                currentMitigation: val as
                  | 'none'
                  | 'backup_gps'
                  | 'manual'
                  | 'other_vps'
                  | 'mission_abort',
              })
            }
            delay={400}
          />
        </div>
      </div>
    </div>
  )
}

interface SelectCardsProps {
  icon: React.ReactNode
  label: string
  sublabel: string
  options: { value: string; label: string; description: string }[]
  value: string
  onChange: (value: string) => void
  delay: number
}

function SelectCards({
  icon,
  label,
  sublabel,
  options,
  value,
  onChange,
  delay,
}: SelectCardsProps) {
  return (
    <div
      className="wizard-stagger space-y-3"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-secondary/50 flex items-center justify-center text-muted-foreground">
          {icon}
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground">{label}</label>
          <p className="text-xs text-muted-foreground/60">{sublabel}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 ml-11">
        {options.map((option) => (
          <button
            key={option.value}
            onClick={() => onChange(option.value)}
            className={cn(
              'p-3 rounded-lg border text-left transition-all',
              value === option.value
                ? 'border-primary bg-primary/5'
                : 'border-border/30 hover:border-border/50'
            )}
          >
            <span className="block text-sm font-medium text-foreground">{option.label}</span>
            <span className="block text-xs text-muted-foreground/60 mt-0.5">
              {option.description}
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}

interface SliderInputProps {
  icon: React.ReactNode
  label: string
  sublabel: string
  value: number
  onChange: (value: number) => void
  suffix: string
  min: number
  max: number
  delay: number
}

function SliderInput({
  icon,
  label,
  sublabel,
  value,
  onChange,
  suffix,
  min,
  max,
  delay,
}: SliderInputProps) {
  return (
    <div
      className="wizard-stagger p-4 rounded-lg border border-border/30"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-secondary/50 flex items-center justify-center text-muted-foreground">
          {icon}
        </div>
        <div className="flex-1">
          <label className="block text-sm font-medium text-foreground mb-1">{label}</label>
          <p className="text-xs text-muted-foreground/60 mb-4">{sublabel}</p>
          <div className="flex items-center gap-4">
            <input
              type="range"
              min={min}
              max={max}
              value={value}
              onChange={(e) => onChange(Number(e.target.value))}
              className="flex-1 accent-primary"
            />
            <span className="w-16 text-right text-lg font-semibold text-foreground">
              {value}
              {suffix}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

interface CurrencyInputProps {
  icon: React.ReactNode
  label: string
  sublabel: string
  value: number
  onChange: (value: number) => void
  delay: number
}

function CurrencyInput({
  icon,
  label,
  sublabel,
  value,
  onChange,
  delay,
}: CurrencyInputProps) {
  const [isFocused, setIsFocused] = useState(false)

  return (
    <div
      className={cn(
        'wizard-stagger p-4 rounded-lg border transition-all',
        isFocused ? 'border-primary/50 bg-primary/5' : 'border-border/30'
      )}
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex items-start gap-4">
        <div
          className={cn(
            'flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-colors',
            isFocused ? 'bg-primary/20 text-primary' : 'bg-secondary/50 text-muted-foreground'
          )}
        >
          {icon}
        </div>
        <div className="flex-1">
          <label className="block text-sm font-medium text-foreground mb-1">{label}</label>
          <p className="text-xs text-muted-foreground/60 mb-3">{sublabel}</p>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-semibold text-muted-foreground">$</span>
            <input
              type="number"
              value={value || ''}
              onChange={(e) => onChange(Number(e.target.value))}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              className={cn(
                'w-40 bg-transparent text-2xl font-semibold text-foreground',
                'border-b-2 transition-colors focus:outline-none',
                isFocused ? 'border-primary' : 'border-border/40'
              )}
              min={0}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

interface NumberInputProps {
  icon: React.ReactNode
  label: string
  sublabel: string
  value: number
  onChange: (value: number) => void
  suffix: string
  delay: number
}

function NumberInput({
  icon,
  label,
  sublabel,
  value,
  onChange,
  suffix,
  delay,
}: NumberInputProps) {
  const [isFocused, setIsFocused] = useState(false)

  return (
    <div
      className={cn(
        'wizard-stagger p-4 rounded-lg border transition-all',
        isFocused ? 'border-primary/50 bg-primary/5' : 'border-border/30'
      )}
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex items-start gap-4">
        <div
          className={cn(
            'flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-colors',
            isFocused ? 'bg-primary/20 text-primary' : 'bg-secondary/50 text-muted-foreground'
          )}
        >
          {icon}
        </div>
        <div className="flex-1">
          <label className="block text-sm font-medium text-foreground mb-1">{label}</label>
          <p className="text-xs text-muted-foreground/60 mb-3">{sublabel}</p>
          <div className="flex items-center gap-3">
            <input
              type="number"
              value={value || ''}
              onChange={(e) => onChange(Number(e.target.value))}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              className={cn(
                'w-32 bg-transparent text-2xl font-semibold text-foreground',
                'border-b-2 transition-colors focus:outline-none',
                isFocused ? 'border-primary' : 'border-border/40'
              )}
              min={0}
            />
            <span className="text-sm text-muted-foreground">{suffix}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

